import { PencilSimple, Plus } from '@phosphor-icons/react';
import type {
  EditPostTextDto,
  Platform as ApiPlatform,
  ScheduledPostDto,
} from '@reelplicate/api-client/model';
import { useEditPostText } from '@reelplicate/api-client/scheduled-posts';
import {
  Button,
  Field,
  Inline,
  Stack,
  TextArea,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useState } from 'react';

import {
  CAPTION_MAX,
  hasTitle,
  length,
  TITLE_MAX,
  type TitledPlatform,
  titleMax,
} from './create-post/form';

const PLATFORM_LABELS: Record<ApiPlatform, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
};

const gone = (status: string) => status === 'PUBLISHED' || status === 'CANCELLED';

type Draft = {
  /** 'all', or one entry per platform when they differ. */
  titles: Map<TitledPlatform | 'all', string>;
  captions: Map<ApiPlatform | 'all', string>;
};

/**
 * A post's text as it goes out - its title on TikTok and YouTube (their own
 * title field) and its caption - and changing it before it goes out. When
 * its accounts carry different text, each platform gets its own field so
 * they aren't flattened into one; accounts it already went out on keep theirs.
 */
export function PostCaption({
  workspaceId,
  post,
  editable,
  onSaved,
}: {
  workspaceId: string;
  post: ScheduledPostDto;
  editable: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const edit = useEditPostText();
  const open = post.accounts.filter((a) => !gone(a.status));
  // A platform's text: the first of its accounts still to go out.
  const captions = new Map<ApiPlatform, string>();
  const titles = new Map<TitledPlatform, string>();
  for (const account of open) {
    if (!captions.has(account.platform)) {
      captions.set(account.platform, account.caption ?? '');
    }
    if (hasTitle(account.platform) && !titles.has(account.platform)) {
      titles.set(account.platform, account.postTitle ?? '');
    }
  }
  const captionsDiffer = new Set(captions.values()).size > 1;
  const titlesDiffer = new Set(titles.values()).size > 1;
  const titled = [...titles.keys()];
  const posted = [
    ...new Set(
      post.accounts
        .filter((a) => a.status === 'PUBLISHED')
        .map((a) => PLATFORM_LABELS[a.platform]),
    ),
  ];
  const [draft, setDraft] = useState<Draft | null>(null);

  const start = () =>
    setDraft({
      titles: titlesDiffer
        ? new Map(titles)
        : new Map(titled.length ? [['all', titles.values().next().value ?? '']] : []),
      captions: captionsDiffer
        ? new Map(captions)
        : new Map([['all', captions.values().next().value ?? post.caption ?? '']]),
    });

  const titleLimit = (key: TitledPlatform | 'all') =>
    key === 'all' ? titleMax(titled) : TITLE_MAX[key]!;
  const tooLong =
    draft !== null &&
    ([...draft.captions.values()].some((t) => length(t) > CAPTION_MAX) ||
      [...draft.titles].some(([key, t]) => length(t) > titleLimit(key)));

  const onSave = async () => {
    if (!draft) return;
    const data: EditPostTextDto = {
      ...(captionsDiffer
        ? { captions: Object.fromEntries(draft.captions) }
        : { caption: draft.captions.get('all') ?? '' }),
      ...(titled.length > 0 &&
        (titlesDiffer
          ? { postTitles: Object.fromEntries(draft.titles) }
          : { postTitle: draft.titles.get('all') ?? '' })),
    };
    try {
      await edit.mutateAsync({ id: workspaceId, postId: post.id, data });
    } catch {
      return; // Shown as a toast.
    }
    await onSaved();
    setDraft(null);
    toast.success(titled.length ? 'Title and caption saved' : 'Caption saved');
  };

  if (draft) {
    return (
      <Stack gap={2}>
        {[...draft.titles].map(([key, text]) => (
          <Field
            key={`title-${key}`}
            label={key === 'all' ? 'Title' : `${PLATFORM_LABELS[key]} title`}
            helper={
              key === 'all'
                ? `In ${titled.map((p) => PLATFORM_LABELS[p]).join(' and ')}’s own title field`
                : undefined
            }
            count={length(text)}
            maxLength={titleLimit(key)}
          >
            <TextInput
              value={text}
              aria-invalid={length(text) > titleLimit(key)}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  titles: new Map(draft.titles).set(key, event.target.value),
                })
              }
            />
          </Field>
        ))}
        {[...draft.captions].map(([key, text]) => (
          <Field
            key={`caption-${key}`}
            label={key === 'all' ? 'Caption' : `${PLATFORM_LABELS[key]} caption`}
            count={length(text)}
            maxLength={CAPTION_MAX}
          >
            <TextArea
              value={text}
              aria-invalid={length(text) > CAPTION_MAX}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  captions: new Map(draft.captions).set(key, event.target.value),
                })
              }
            />
          </Field>
        ))}
        {posted.length > 0 && (
          <p className="text-caption text-text-tertiary">
            Already posted on {posted.join(' and ')}; changes apply to the others.
          </p>
        )}
        <Inline gap={2}>
          <Button
            size="sm"
            loading={edit.isPending}
            disabled={tooLong}
            onClick={() => void onSave()}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={edit.isPending}
            onClick={() => setDraft(null)}
          >
            Cancel
          </Button>
        </Inline>
      </Stack>
    );
  }

  const canEdit = editable && open.length > 0;
  // Titles as they show: one, or each platform's when they differ.
  const shownTitles = post.accounts.flatMap((a) =>
    hasTitle(a.platform) && a.postTitle ? [[a.platform, a.postTitle] as const] : [],
  );
  const distinctTitles = [...new Map(shownTitles).entries()];
  const sameTitle = new Set(distinctTitles.map(([, t]) => t)).size === 1;
  const editLabel = titled.length ? 'Edit title and caption' : 'Edit caption';

  if (!post.caption && distinctTitles.length === 0) {
    return canEdit ? (
      <Button
        size="sm"
        variant="ghost"
        leadingIcon={Plus}
        className="self-start"
        onClick={start}
      >
        {titled.length ? 'Add title and caption' : 'Add caption'}
      </Button>
    ) : null;
  }
  return (
    <Stack gap={1} align="left">
      {sameTitle && distinctTitles[0] ? (
        <p className="text-body-sm text-text-primary font-semibold">
          {distinctTitles[0][1]}
        </p>
      ) : (
        distinctTitles.map(([platform, title]) => (
          <p key={platform} className="text-body-sm text-text-primary">
            <span className="font-semibold">{title}</span>
            <span className="text-text-tertiary"> · {PLATFORM_LABELS[platform]}</span>
          </p>
        ))
      )}
      {post.caption && (
        <p className="text-body-sm text-text-secondary whitespace-pre-line">
          {post.caption}
        </p>
      )}
      {canEdit && (
        <Button size="sm" variant="ghost" leadingIcon={PencilSimple} onClick={start}>
          {editLabel}
        </Button>
      )}
    </Stack>
  );
}
